/**
 * Types for the customer-facing docs feature.
 *
 * Documents are organized by category (label taxonomy) and rendered
 * from an inventory structure that groups documents by program type.
 */

/** Well-known category labels with a guaranteed display order */
export type KnownDocCategory = "shared" | "ma" | "ma-catalog" | "ebt" | "medicaid" | "rewards";

/** Any Confluence label can be a category — known ones get preferred ordering */
export type DocCategory = string;

/** Preferred display order for known categories. New labels discovered from
 *  the inventory are appended alphabetically after these. */
export const KNOWN_CATEGORY_ORDER: KnownDocCategory[] = [
  "shared", "ma", "ma-catalog", "rewards", "ebt", "medicaid",
];

/** A single document in the inventory */
export interface DocItem {
  /** Unique slug used in the URL path */
  slug: string;
  /** Display title */
  title: string;
  /** Category label for grouping in the sidebar */
  category: DocCategory;
  /** Optional description shown beneath the title */
  description?: string;
}

/** A group of documents sharing a category label */
export interface DocCategoryGroup {
  /** The category label */
  category: DocCategory;
  /** Human-readable label for the category */
  label: string;
  /** Documents in this category */
  items: DocItem[];
}

/** Display labels for known categories. Unknown labels are title-cased automatically. */
export const CATEGORY_LABELS: Record<string, string> = {
  shared: "Shared",
  ma: "Medicare Advantage",
  "ma-catalog": "Catalog",
  ebt: "EBT",
  medicaid: "Medicaid",
  rewards: "Rewards",
};

/** Return a human-readable label for any category string. Known categories use
 *  CATEGORY_LABELS; unknown ones are title-cased (e.g. "snap-benefits" → "Snap Benefits"). */
export function getCategoryLabel(cat: string): string {
  return CATEGORY_LABELS[cat] ?? cat.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Inventory page from the backend API */
export interface DocsInventoryPage {
  id: string;
  title: string;
  labels: string[];
  last_updated: string;
}

/** Full page content from the backend API */
export interface DocsPageContent {
  id: string;
  title: string;
  labels: string[];
  content: string;
  last_updated: string;
}

/** Auth user for the docs feature (separate from main app auth) */
export interface DocsUser {
  /** User display name */
  name: string;
  /** User email */
  email: string;
  /** Whether this is an internal (Evermore) user */
  isInternal: boolean;
  /** Client type for external users (null for internal) */
  clientType: string | null;
  /** Customer name (for external users) */
  customerName?: string;
  /** Magic link scope: "all" | "folder" | "page". Always "all" for internal users. */
  scope: "all" | "folder" | "page";
  /** For scope="folder": the folder_id (Confluence label) this session is scoped to */
  folderId?: string;
  /** For scope="page": the page_id this session is scoped to */
  pageId?: string;
}

/** Auth context value for the docs feature */
export interface DocsAuthContextValue {
  /** Current authenticated user (null if not authenticated) */
  user: DocsUser | null;
  /** Whether auth is still loading */
  isLoading: boolean;
  /** Whether the user is an internal Evermore user */
  isInternal: boolean;
  /** Client type for external users */
  clientType: string | null;
  /** Logout function */
  logout: () => void;
  /** Magic link scope: "all" | "folder" | "page". Always "all" for internal users. */
  scope: "all" | "folder" | "page";
  /** For scope="folder": the folder_id (Confluence label) this session is scoped to */
  folderId?: string;
  /** For scope="page": the page_id this session is scoped to */
  pageId?: string;
}
